import { Divider } from '@droplet-tech-code/core-elements/module/ui/Divider/Divider';
import { useModalVerify } from '@droplet-tech-code/core-elements/module/ui/Modal';
import { VStack, HStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { dayjs } from '@droplet-tech-code/core-elements/module/utils/utils.date';
import {
  BookingClientWithPurchasesAndTickets,
  SupportRequestBackendClient,
} from '@naus-code/naus-admin-types';
import { useCallback } from 'react';
import { Field } from '~/components/Card/Field';
import { AppText } from '~/components/Text';
import { bookingApi } from '~/screens/Support/Bookings/Bookings.api';
import { getPriceWithSymbol } from '~/screens/Support/Purchases/purchase.utils';
import { supportRequestActionsApi } from '~/screens/Support/Requests/supportRequestActions.api';
import { DATE_TIME_SERVER_FORMAT } from '~/utils/date.utils';
import { translate } from '~/utils/translation.utils';
import { ClickableListItem } from '../utils';
import { useGetCfarQuote } from '../../Modals/CFAR.NewQuote';
import { Text } from '@droplet-tech-code/core-elements/module/ui/Text';
import { View } from '@droplet-tech-code/core-elements/module/ui/View';

export const CfarQuoteSection = ({
  supportRequestInfo,
  check,
}: {
  supportRequestInfo: SupportRequestBackendClient;
  check: string;
}) => {
  const { data } = bookingApi.useGetBookingQuery(supportRequestInfo.bookingId || '', {
    skip: !supportRequestInfo.bookingId,
  });

  const { bookingId, _id: supportId } = supportRequestInfo;
  const cfarQuoteDetails = mapBookingToCfarQuoteDetails(data?.booking);
  const { open: openCfarQuoteModal } = useGetCfarQuote({
    bookingId,
    currency: data?.booking.paymentSessions[0].paymentSummary.currency,
    supportId,
    check,
    cfarDetails: cfarQuoteDetails,
  });
  const cfarSessionType = translate('supportRequests.koala.manual');
  const currentSession =
    supportRequestInfo.manualCfarSession ||
    //@ts-expect-error
    supportRequestInfo.requestOptions?.bookingProtection;

  const { verifyAction } = useModalVerify();
  const [subscribeCfar, { isLoading: isSubbing }] =
    supportRequestActionsApi.useSubscribeCfarQuoteMutation();

  const [cancelCfar, { isLoading: isCancelling }] =
    supportRequestActionsApi.useCancelCfarQuoteMutation();

  // Verify modal
  const openQuoteActionVerify = useCallback(
    async (subscribeQuote: boolean) => {
      await verifyAction({
        title: subscribeQuote
          ? translate('supportRequests.subscribeCfarVerify')
          : translate('supportRequests.cancelCfarVerify'),
      }).then((verify) => {
        if (verify) {
          if (subscribeQuote) {
            subscribeCfar({
              supportId,
              check,
              bookingId: bookingId!,
            });
            return;
          }
          cancelCfar({ supportId, check, bookingId: bookingId! });
        }
      });
    },
    [check, supportId],
  );

  if (!data?.booking || !data?.booking.bookingProtection) {
    return null;
  }

  if (currentSession?.errors?.length) {
    const lastError = currentSession.errors?.at(-1);
    return (
      <VStack space="2.5" p="2.5">
        <VStack br={15} bg={'error-extraLight'} p="2.5">
          <VStack m="1" space="2">
            <AppText.ExtraSmall color="monochrome-extraDark" flex={1}>
              {`${translate('supportRequests.cfarSession')} - ${cfarSessionType}`}
            </AppText.ExtraSmall>
            <Text.Small>{lastError}</Text.Small>
          </VStack>
          <HStack style={{ alignContent: 'stretch' }} flex={1}>
            <View flex={1} />
            <ClickableListItem
              color="success-dark"
              isLoading={isCancelling}
              onPress={async () => await openQuoteActionVerify(false)}
            >
              {` ${translate('supportRequests.cancelQuote')}`}
            </ClickableListItem>
          </HStack>
        </VStack>
        <Divider />
        <ClickableListItem
          icon="euro"
          color="primary-mid"
          onPress={() => {
            openCfarQuoteModal();
          }}
        >
          {` ${translate('supportRequests.newCfarQuote')}`}
        </ClickableListItem>
      </VStack>
    );
  }

  return (
    <VStack space="2.5">
      {currentSession ? (
        <VStack space="2.5" p="2.5">
          <VStack br={15} bg={'success-extraLight'} p="2.5">
            <VStack m="1" space="2">
              <AppText.ExtraSmall color="monochrome-extraDark" flex={1}>
                {`${translate('supportRequests.cfarSession')} - ${cfarSessionType}`}
              </AppText.ExtraSmall>
              <Field
                label={translate('supportRequests.cfarDiff')}
                labelType="extraSmall"
                labelColor="black"
                value={getPriceWithSymbol(
                  currentSession.valueDiff,
                  data.booking.paymentSessions[0]?.paymentSummary.currency ?? '',
                )}
                valueType="extraSmall"
              />
              <Field
                label={translate('supportRequests.dateCreated')}
                labelColor="black"
                labelType="extraSmall"
                value={dayjs(currentSession.timestamp).format(DATE_TIME_SERVER_FORMAT)}
                valueType="extraSmall"
              />
            </VStack>
            <HStack style={{ alignContent: 'stretch' }} flex={1}>
              <ClickableListItem
                color="success-dark"
                isLoading={isSubbing}
                onPress={async () => await openQuoteActionVerify(true)}
              >
                {`${translate('supportRequests.subscribeQuote')}`}
              </ClickableListItem>
              <View flex={1} />
              <ClickableListItem
                color="success-dark"
                isLoading={isCancelling}
                onPress={async () => await openQuoteActionVerify(false)}
              >
                {` ${translate('supportRequests.cancelQuote')}`}
              </ClickableListItem>
            </HStack>
          </VStack>
          <Divider />
        </VStack>
      ) : null}
      <ClickableListItem
        icon="euro"
        color="primary-mid"
        onPress={() => {
          openCfarQuoteModal();
        }}
      >
        {` ${translate('supportRequests.newCfarQuote')}`}
      </ClickableListItem>
      <Divider />
    </VStack>
  );
};

const mapBookingToCfarQuoteDetails = (booking?: BookingClientWithPurchasesAndTickets) => {
  if (!booking) {
    return undefined;
  }
  const { purchases } = booking;
  const firstPurchase = purchases.reduce((purchase, cache) => {
    if (dayjs(purchase.startTimestamp).isBefore(cache.startTimestamp)) {
      return purchase;
    }
    return cache;
  }, purchases[0]);

  const passengerCount = firstPurchase.tickets.filter(
    (ticket) => ticket.ticketType === 'Passenger',
  ).length;

  return {
    departureDateTime: dayjs(firstPurchase.startTimestamp).format(
      DATE_TIME_SERVER_FORMAT,
    ),
    passengerCount,
  };
};
